// Content data for each Education sub-page

export const EDUCATION_PROGRAMS = {
  "vre": {
    slug: "vre",
    parentSlug: "/education",
    parentLabel: "Education",
    eyebrow: "Education Assistance",
    title: "Vocational Rehabilitation & Employment (VR&E)",
    tagline: "Turn your service-connected challenges into a new career path.",
    intro:
      "VR&E (Chapter 31) is a powerful VA program that helps veterans with a service-connected disability rating prepare for, find, and keep suitable employment. We walk you through eligibility, entitlement, and how to prepare for your first meeting so you make the most of it.",
    whoFor: [
      "Veterans with a service-connected disability rating",
      "Veterans considering a career change",
      "Veterans returning to school after separation",
      "Active duty preparing for transition",
    ],
    benefits: [
      "Tuition, fees, books, and supplies covered under an approved plan",
      "Monthly subsistence allowance",
      "Career counseling and job placement support",
      "Options for on-the-job training and apprenticeships",
    ],
    faqs: [
      { q: "Do I qualify for VR&E?", a: "You generally need at least a 10% service-connected rating and an employment handicap. Eligibility rules can be nuanced — we help you understand where you stand before you apply." },
      { q: "How is VR&E different from the GI Bill?", a: "VR&E is career-focused and requires a service-connected disability. It often provides deeper support than the GI Bill, including career counseling and equipment. In some cases you may qualify for both — we help you decide which to use." },
    ],
  },
  "gi-bill": {
    slug: "gi-bill",
    parentSlug: "/education",
    parentLabel: "Education",
    eyebrow: "Education Assistance",
    title: "GI Bill Resources",
    tagline: "Understand your benefits and how to maximize them.",
    intro:
      "The Post-9/11 GI Bill and Montgomery GI Bill are two of the most valuable benefits earned through service — but they're often used without a plan. We help you understand what you have, what it covers, and how to make it stretch further.",
    whoFor: [
      "Veterans and current service members with GI Bill eligibility",
      "Dependents receiving transferred benefits",
      "Anyone weighing school options and payment plans",
    ],
    benefits: [
      "Guidance on transferability and eligibility windows",
      "Understanding Yellow Ribbon Program schools",
      "How housing allowance (BAH) applies to your program",
      "Coordination with VR&E, scholarships, and grants",
    ],
    faqs: [
      { q: "Can I use my GI Bill and VR&E?", a: "Sometimes, but with important tradeoffs. We walk through the pros/cons so you don't burn benefits unnecessarily." },
      { q: "Does it cover trade school and certifications?", a: "Yes — many trade programs and industry certifications are approved. We help you check whether your target program qualifies." },
    ],
  },
  "college-planning": {
    slug: "college-planning",
    parentSlug: "/education",
    parentLabel: "Education",
    eyebrow: "Education Assistance",
    title: "College Planning",
    tagline: "Program comparison, application prep, and admissions strategy.",
    intro:
      "Choosing the right college — public, private, online, or hybrid — is one of the biggest financial decisions you'll make. We help you compare programs, evaluate cost, and prepare a strong application.",
    whoFor: [
      "First-generation college students",
      "Veterans and their dependents",
      "Working adults returning to school",
      "Career changers considering a degree program",
    ],
    benefits: [
      "Side-by-side comparison of schools and programs",
      "Application timeline and essay coaching",
      "Financial aid navigation (FAFSA, GI Bill, scholarships)",
      "Fit assessment based on your goals and lifestyle",
    ],
    faqs: [
      { q: "Do you help with applications directly?", a: "We coach, review, and structure your application materials — the submission is yours, but you won't do it alone." },
      { q: "Is online college worth it?", a: "It can be, depending on the program and your goals. We help you spot quality programs and avoid credential-mill traps." },
    ],
  },
  "trade-school": {
    slug: "trade-school",
    parentSlug: "/education",
    parentLabel: "Education",
    eyebrow: "Education Assistance",
    title: "Trade School Planning",
    tagline: "Explore accelerated career pathways in skilled trades.",
    intro:
      "Trade school can be faster, more affordable, and lead to strong-paying careers — but not every program is worth your time. We help you evaluate options, check accreditation, and align a trade with your goals.",
    whoFor: [
      "Adults looking for a career change without a 4-year degree",
      "Veterans exploring trades post-service",
      "High school graduates weighing college vs. a trade",
    ],
    benefits: [
      "Program vetting and accreditation checks",
      "Cost comparison and payment planning",
      "Industry outlook briefings (electrical, HVAC, welding, CDL, etc.)",
      "Coordination with VR&E and GI Bill benefits",
    ],
    faqs: [
      { q: "Can I use my GI Bill for a trade program?", a: "Often, yes — as long as the school is VA-approved. We help you verify before you enroll." },
    ],
  },
  "career-transition": {
    slug: "career-transition",
    parentSlug: "/education",
    parentLabel: "Education",
    eyebrow: "Education Assistance",
    title: "Career Transition",
    tagline: "Bridge from service to civilian career with a clear plan.",
    intro:
      "Transitioning out of service — or between careers — is one of the hardest inflection points in life. We help you make it deliberate: clarify your goals, build a resume that speaks civilian, and target opportunities that fit.",
    whoFor: [
      "Service members within 12 months of separation",
      "Recently separated veterans",
      "Mid-career professionals considering a pivot",
    ],
    benefits: [
      "Career goal clarification and prioritization",
      "Resume translation (military → civilian language)",
      "Interview preparation and networking guidance",
      "Alignment with education and benefit programs",
    ],
    faqs: [
      { q: "When should I start transition planning?", a: "Ideally 12+ months before separation — earlier is better. But it's never too late to make the transition more intentional." },
    ],
  },
  "scholarships": {
    slug: "scholarships",
    parentSlug: "/education",
    parentLabel: "Education",
    eyebrow: "Education Assistance",
    title: "Scholarship Resources",
    tagline: "Find and prepare applications for veteran and civilian scholarships.",
    intro:
      "Scholarships are often left on the table because the search feels overwhelming. We help you identify the ones you're most likely to win — and prepare applications that stand out.",
    whoFor: [
      "Veterans and dependents seeking to reduce out-of-pocket costs",
      "First-generation students",
      "Adults returning to school",
    ],
    benefits: [
      "Curated list of veteran and civilian scholarship opportunities",
      "Essay coaching and review",
      "Application timeline building",
      "Follow-up strategy and tracking",
    ],
    faqs: [
      { q: "Do you find scholarships for me?", a: "We help you find and prioritize scholarships that match your profile — you apply, we coach." },
    ],
  },
};

export const EDUCATION_PROGRAMS_LIST = Object.values(EDUCATION_PROGRAMS);
